/**
 * ChallengeBriefPage — Page "Règles du jeu" d'un challenge inter
 * Layout pleine largeur avec nav latérale + cards offres/personae
 * Challenges V2 (Lot 5) — refonte UX du 10/04/2026
 */

import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Button } from "./ui/button";
import {
  Building2, Package, UserCircle, Target, Award, Gift, Calendar,
  ArrowLeft, Play, Loader2, AlertCircle, Star, Clock, Crosshair,
  Trophy, Sparkles, BookOpen, Gamepad2, Info,
} from "lucide-react";
import { challengesApi } from "../lib/api";
import { pickOfferLabel, pickOfferDescription } from "../lib/offer-i18n";
import {
  pickChallengeTitle,
  pickChallengeDescription,
  pickChallengeFictiveCompanyName,
  pickChallengeFictiveCompanyDescription,
  pickChallengeGiftName,
  pickChallengeGiftDescription,
} from "../lib/challenge-i18n";
import { localizePersonalityStyle } from "../lib/persona-personality";
import { getPersonaLabel, pickDecisionMaker } from "./PersonaUtils";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import { pickScoringLabel } from "../lib/scoring-i18n";
import { pickPersonaField } from "../lib/persona-i18n";

interface ChallengeBriefPageProps {
  challengeId: string;
  onBack: () => void;
  onJoin?: () => void;
  isEnrolled?: boolean;
}

/** Résout un code objectif en label lisible */
function resolveObjectiveLabel(
  obj: string | undefined,
  t: (key: string) => string,
  isFr: boolean,
  scoringCriteria?: any[],
): string {
  if (!obj) return "";
  const keyMap: Record<string, string> = {
    most_rdv: "challenge.objectiveMostRdv",
    most_needs: "challenge.objectiveMostNeeds",
    deal_closed: "challenge.objectiveDealClosed",
    pitch_validated: "challenge.objectivePitchValidated",
    tension_mastered: "challenge.objectiveTensionMastered",
    clear_outcome: "challenge.objectiveClearOutcome",
    best_average: "challenge.objectiveBestAverage",
    cumulative_score: "challenge.objectiveCumulativeScore",
  };
  if (keyMap[obj]) return t(keyMap[obj]);
  if (scoringCriteria) {
    const c = scoringCriteria.find((sc: any) => sc.key === obj);
    if (c) return pickScoringLabel(c, isFr ? "fr" : "en");
  }
  return obj;
}

const SECTION_IDS = [
  { id: "mission", icon: "🎯", labelKey: "challenge.brief.yourMission" },
  { id: "company", icon: "🏢", labelKey: "challenge.brief.theCompany" },
  { id: "offers", icon: "📦", labelKey: "challenge.brief.offers" },
  { id: "profiles", icon: "👤", labelKey: "challenge.brief.profiles" },
  { id: "rules", icon: "📋", labelKey: "challenge.brief.rules" },
  { id: "evaluation", icon: "📊", labelKey: "challenge.brief.evaluation" },
  { id: "rewards", icon: "🏆", labelKey: "challenge.brief.rewards" },
];

export function ChallengeBriefPage({ challengeId, onBack, onJoin, isEnrolled }: ChallengeBriefPageProps) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";

  const [challenge, setChallenge] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    challengesApi.getChallenge(challengeId)
      .then((ch: any) => setChallenge(ch))
      .catch((err: any) => setError(err.message))
      .finally(() => setLoading(false));
  }, [challengeId]);

  const scrollTo = (id: string) => {
    document.getElementById(`brief-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  if (error || !challenge) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="h-8 w-8 mx-auto mb-3 text-red-400" />
        <p className="text-red-600">{error || t("challenge.brief.challengeNotFound")}</p>
        <Button variant="outline" onClick={onBack} className="mt-4"><ArrowLeft className="h-4 w-4 mr-2" />{t("common.back")}</Button>
      </div>
    );
  }

  const offers = challenge.offers || [];
  const personae = challenge.personae || [];
  const badge = challenge.badge;
  // Lecture i18n FR/EN du contenu saisi par l'organisateur
  const challengeTitle = pickChallengeTitle(challenge, i18n.language);
  const challengeDescription = pickChallengeDescription(challenge, i18n.language);
  const fictiveCompanyName = pickChallengeFictiveCompanyName(challenge, i18n.language);
  const fictiveCompanyDescription = pickChallengeFictiveCompanyDescription(challenge, i18n.language);
  const giftName = pickChallengeGiftName(challenge, i18n.language);
  const giftDescription = pickChallengeGiftDescription(challenge, i18n.language);
  const hasGift = giftName?.trim();
  const scoringCriteria = challenge.simulatorScoringCriteria || [];
  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString(isFr ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";
  const primaryLabel = resolveObjectiveLabel(challenge.primaryObjective, t, isFr, scoringCriteria);
  const secondaryLabel = resolveObjectiveLabel(challenge.secondaryObjective, t, isFr, scoringCriteria);
  const simName = (challenge.simulatorName || "").toLowerCase();
  const isServiceClient = simName.includes("satisfactor") || simName.includes("service client") || simName.includes("customer care") || simName.includes("sav");

  return (
    <div className="min-h-screen">
      {/* HERO — pleine largeur */}
      <div className="w-full" style={{ background: "linear-gradient(135deg, #384872 0%, #5A4BBE 50%, #988EF2 100%)" }}>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-white bg-white/20 hover:bg-white/30 mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />{t("challenge.brief.backToChallenge")}
          </Button>
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-6 w-6 text-[#F6C847]" />
            <span className="text-white/70 text-sm font-medium uppercase tracking-wide">
              {t("challenge.brief.challengeBriefRules")}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "Sora, sans-serif" }}>
            {challengeTitle}
          </h1>
          {challengeDescription && <p className="text-white/80 text-lg max-w-3xl">{challengeDescription}</p>}
          <div className="flex flex-wrap gap-3 mt-6">
            {challenge.startDate && (
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2 text-white text-sm">
                <Calendar className="h-4 w-4 text-[#F6C847]" />
                {formatDate(challenge.startDate)}{challenge.endDate ? ` → ${formatDate(challenge.endDate)}` : ""}
              </div>
            )}
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2 text-white text-sm">
              <Target className="h-4 w-4 text-[#F6C847]" />
              {challenge.maxSessions} {t("challenge.brief.sessionsToComplete")}
            </div>
            {challenge.simulatorName && (
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2 text-white text-sm">
                <Gamepad2 className="h-4 w-4 text-[#F6C847]" />
                {challenge.simulatorName}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BODY — 2 colonnes : nav gauche + contenu */}
      <div className="max-w-6xl mx-auto px-6 py-8 flex gap-8">
        {/* Nav latérale (desktop) */}
        <nav className="hidden lg:block w-48 shrink-0 sticky top-20 self-start">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t("challenge.brief.contents")}</p>
          <div className="space-y-1">
            {SECTION_IDS.map((s) => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors text-muted-foreground hover:text-[#384872]">
                <span>{s.icon}</span>
                <span>{t(s.labelKey)}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Contenu principal */}
        <div className="flex-1 min-w-0 space-y-10">

          {/* 1. VOTRE MISSION */}
          <section id="brief-mission">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
              <span className="text-2xl">🎯</span> {t("challenge.brief.yourMission")}
            </h2>
            <div className="p-5 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 text-base leading-relaxed">
              <Trans
                i18nKey={isServiceClient ? "challenge.brief.missionServiceClientFr" : "challenge.brief.missionSalesFr"}
                values={{
                  company: fictiveCompanyName || t("challenge.brief.fictiveCompanyFallback"),
                  count: challenge.maxSessions,
                }}
                components={{ 1: <strong />, 3: <strong /> }}
              />
            </div>
          </section>

          {/* 2. L'ENTREPRISE */}
          {fictiveCompanyName && (
            <section id="brief-company">
              <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
                <span className="text-2xl">🏢</span> {t("challenge.brief.theCompany")}
              </h2>
              <div className="p-6 rounded-xl border-2 border-[#384872]/20 bg-slate-50">
                <h3 className="text-2xl font-bold" style={{ color: "#384872" }}>{fictiveCompanyName}</h3>
                {fictiveCompanyDescription && (
                  <p className="text-muted-foreground mt-3 whitespace-pre-line leading-relaxed">{fictiveCompanyDescription}</p>
                )}
              </div>
            </section>
          )}

          {/* 3. LES OFFRES — cards */}
          {offers.length > 0 && (
            <section id="brief-offers">
              <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
                <span className="text-2xl">📦</span> {isServiceClient ? t("challenge.brief.offersHeadingServiceClient") : t("challenge.brief.offersHeadingSales")}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {isServiceClient ? t("challenge.brief.offersHintServiceClient") : t("challenge.brief.offersHintSales")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {offers.map((offer: any, i: number) => (
                  <div key={offer.id || i} className="rounded-xl border-2 border-[#7CBF94]/30 bg-white p-5 hover:shadow-lg transition-shadow">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: "#7CBF94" }}>
                      <Package className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-bold text-base" style={{ color: "#384872" }}>{pickOfferLabel(offer, i18n.language)}</h3>
                    {pickOfferDescription(offer, i18n.language) && <p className="text-sm text-muted-foreground mt-2">{pickOfferDescription(offer, i18n.language)}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 4. LES PROFILS — cards */}
          {personae.length > 0 && (
            <section id="brief-profiles">
              <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
                <span className="text-2xl">👤</span> {t("challenge.brief.profilesHeading")}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {t("challenge.brief.profilesHint")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {personae.map((p: any, i: number) => {
                  const isCC = p.category === "customer_care";
                  return (
                    <div key={p.id || i} className="rounded-xl border-2 border-[#988EF2]/30 bg-white p-5 hover:shadow-lg transition-shadow">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#988EF2" }}>
                          <UserCircle className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-bold" style={{ color: "#384872" }}>{getPersonaLabel(p) || t("challenge.brief.profileClientFallback")}</p>
                          <p className="text-xs text-muted-foreground">{p.personaType} — {isCC ? t("challenge.brief.categoryCustomerCare") : t("challenge.brief.categorySales")}</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        {p.decisionMakerProfile && <p>👤 {pickDecisionMaker(p, i18n.language.startsWith("en") ? "en" : "fr")}</p>}
                        {p.painPoints && <p>🎯 {pickPersonaField(p, "painPoints", i18n.language)}</p>}
                        {p.ageRange && <p>📊 {t("challenge.brief.fieldAge")}: {p.ageRange}</p>}
                        {p.purchaseCriteria && <p>💡 {t("challenge.brief.fieldCriteria")}: {p.purchaseCriteria}</p>}
                        {p.contactReason && <p>📞 {t("challenge.brief.fieldReason")}: {localizePersonaOption(p.contactReason, i18n.language)}</p>}
                        {p.personalityStyle && <p>🎭 {t("persona.personalityStyle")}: {localizePersonalityStyle(p.personalityStyle, i18n.language)?.label}</p>}
                        {p.expressionStyle && !p.personalityStyle && <p>🗣️ {t("challenge.brief.fieldStyle")}: {localizePersonaOption(p.expressionStyle, i18n.language)}</p>}
                        {p.mainExpectation && <p>⭐ {t("challenge.brief.fieldExpectation")}: {localizePersonaOption(p.mainExpectation, i18n.language)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 5. LES RÈGLES DU JEU */}
          <section id="brief-rules">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
              <span className="text-2xl">📋</span> {t("challenge.brief.rulesHeading")}
            </h2>
            <div className="p-5 rounded-xl bg-amber-50/50 border border-amber-200 space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-lg">🎮</span>
                <p className="text-sm">
                  <Trans
                    i18nKey="challenge.brief.rulesSessionsCount"
                    values={{ count: challenge.maxSessions }}
                    components={{ 1: <strong />, 3: <strong /> }}
                  />
                </p>
              </div>
              {challenge.difficulty && (
                <div className="flex items-start gap-3">
                  <span className="text-lg">💪</span>
                  <p className="text-sm">
                    <Trans
                      i18nKey="challenge.brief.rulesDifficulty"
                      values={{
                        level: challenge.difficulty === "easy"
                          ? t("challenge.brief.difficultyEasy")
                          : challenge.difficulty === "hard"
                          ? t("challenge.brief.difficultyHard")
                          : t("challenge.brief.difficultyMedium"),
                      }}
                      components={{ 1: <strong /> }}
                    />
                  </p>
                </div>
              )}
              <div className="flex items-start gap-3">
                <span className="text-lg">⚠️</span>
                <p className="text-sm">
                  <Trans
                    i18nKey="challenge.brief.rulesPlayButton"
                    components={{ 1: <strong />, 3: <strong /> }}
                  />
                </p>
              </div>
              {challenge.orangeThresholdMinutes && (
                <div className="flex items-start gap-3">
                  <span className="w-4 h-4 rounded-full bg-orange-400 mt-1 shrink-0" />
                  <p className="text-sm">
                    <Trans
                      i18nKey="challenge.brief.rulesOrangeThreshold"
                      values={{ minutes: challenge.orangeThresholdMinutes }}
                      components={{ 1: <strong /> }}
                    />
                  </p>
                </div>
              )}
              {challenge.redThresholdMinutes && (
                <div className="flex items-start gap-3">
                  <span className="w-4 h-4 rounded-full bg-red-400 mt-1 shrink-0" />
                  <p className="text-sm">
                    <Trans
                      i18nKey="challenge.brief.rulesRedThreshold"
                      values={{ minutes: challenge.redThresholdMinutes }}
                      components={{ 1: <strong /> }}
                    />
                  </p>
                </div>
              )}
              {challenge.startDate && challenge.endDate && (
                <div className="flex items-start gap-3">
                  <span className="text-lg">🚀</span>
                  <p className="text-sm">
                    <Trans
                      i18nKey="challenge.brief.rulesKickoff"
                      values={{
                        start: formatDate(challenge.startDate),
                        end: formatDate(challenge.endDate),
                      }}
                      components={{ 1: <strong />, 3: <strong /> }}
                    />
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* 6. ÉVALUATION */}
          <section id="brief-evaluation">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
              <span className="text-2xl">📊</span> {t("challenge.brief.evaluationHeading")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {primaryLabel && (
                <div className="p-5 rounded-xl border-2 border-amber-200 bg-amber-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="h-5 w-5 text-[#F6C847]" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">{t("challenge.brief.primaryObjective")}</span>
                  </div>
                  <p className="font-bold text-lg" style={{ color: "#384872" }}>{primaryLabel}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("challenge.brief.primaryObjectiveHint")}
                  </p>
                </div>
              )}
              {secondaryLabel && (
                <div className="p-5 rounded-xl border-2 border-purple-200 bg-purple-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Crosshair className="h-5 w-5 text-[#988EF2]" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-purple-600">{t("challenge.brief.secondaryObjective")}</span>
                  </div>
                  <p className="font-bold text-lg" style={{ color: "#384872" }}>{secondaryLabel}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("challenge.brief.secondaryObjectiveHint")}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* 7. RÉCOMPENSES */}
          {(badge || hasGift) && (
            <section id="brief-rewards">
              <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
                <span className="text-2xl">🏆</span> {t("challenge.brief.rewardsHeading")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {badge && (
                  <div className="p-5 rounded-xl border-2 border-[#F6C847]/40 bg-yellow-50/50 hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl border-2 border-[#F6C847]/30 overflow-hidden bg-white p-2 shrink-0">
                        <img src={badge.imageUrl} alt={badge.name} className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <p className="text-xs text-[#926B00] font-semibold uppercase tracking-wide">{t("challenge.brief.badgeForFinishers")}</p>
                        <p className="font-bold text-lg" style={{ color: "#384872" }}>{badge.name}</p>
                        {badge.description && <p className="text-sm text-muted-foreground">{badge.description}</p>}
                      </div>
                    </div>
                  </div>
                )}
                {hasGift && (
                  <div className="p-5 rounded-xl border-2 border-[#F6C847]/40 bg-yellow-50/50 hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-4">
                      {challenge.giftPhotoUrl && (
                        <div className="w-16 h-16 rounded-xl border overflow-hidden bg-white shrink-0">
                          <img src={challenge.giftPhotoUrl} alt={giftName} className="w-full h-full object-contain" />
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-[#926B00] font-semibold uppercase tracking-wide">{t("challenge.brief.giftForWinner")}</p>
                        <p className="font-bold text-lg" style={{ color: "#384872" }}>{giftName}</p>
                        {giftDescription && <p className="text-sm text-muted-foreground">{giftDescription}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* CTA */}
          <div className="text-center py-8">
            {onJoin && !isEnrolled && challenge.status === "active" && (
              <Button onClick={onJoin} size="lg"
                className="text-lg px-10 py-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                style={{ backgroundColor: "#F6C847", color: "#926B00" }}>
                <Play className="h-6 w-6 mr-2" />
                {t("challenge.brief.joinChallenge")}
              </Button>
            )}
            {isEnrolled && (
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 border border-green-200 rounded-xl px-6 py-3 text-sm font-medium">
                ✓ {t("challenge.brief.enrolledInChallenge")}
              </div>
            )}
            {challenge.status === "draft" && (
              <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 border border-gray-200 rounded-xl px-6 py-3 text-sm">
                {t("challenge.brief.challengeNotActiveYet")}
              </div>
            )}
            {challenge.status === "paused" && (
              <div className="inline-flex items-center gap-2 rounded-xl border px-6 py-3 text-sm" style={{ background: "#FBF1D6", color: "#926B00", borderColor: "#F6C847" }}>
                {t("challenge.brief.challengePaused")}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
